/**
 * SwapAnalytics page-view beacon (site-wide — every route on cars.swapp1990.org
 * is box3d-relevant: /ball-lab, /wrecking-yard, etc; `path` in metadata
 * distinguishes them).
 *
 * Fire-and-forget POST to https://analytics.swapp1990.org/api/events.
 * Fires once per page load and again on every SPA route change, deduped on
 * identical consecutive paths. Every code path is wrapped so this module can
 * NEVER throw into the app, and never blocks rendering (scheduled after
 * first paint / on idle).
 */

const ENDPOINT = 'https://analytics.swapp1990.org/api/events';
const APP_ID = 'box3d';
const DEVICE_KEY = 'swapanalytics_device_id';
const TIMEOUT_MS = 5000;

interface BeaconPayload {
  appId: string;
  eventName: string;
  environment: 'production' | 'development';
  deviceId: string;
  metadata: {
    path: string;
    referrer: string | null;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_content?: string;
  };
}

let lastPath: string | null = null;

function uuid(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // fall through to manual uuid
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getDeviceId(): string {
  try {
    let id = window.localStorage.getItem(DEVICE_KEY);
    if (!id) {
      id = uuid();
      window.localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch {
    return uuid();
  }
}

function detectEnvironment(): 'production' | 'development' {
  // The analytics backend enforces environment in {production, testflight,
  // development} — "dev" is rejected with a 400. See verification notes.
  try {
    return window.location.host === 'cars.swapp1990.org' ? 'production' : 'development';
  } catch {
    return 'development';
  }
}

function utmMetadata(): Record<string, string> {
  const out: Record<string, string> = {};
  try {
    const params = new URLSearchParams(window.location.search);
    for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'] as const) {
      const value = params.get(key);
      if (value) out[key] = value;
    }
  } catch {
    // malformed query string — ship without utm fields
  }
  return out;
}

function buildPayload(path: string): BeaconPayload {
  let referrer: string | null = null;
  try {
    referrer = document.referrer || null;
  } catch {
    referrer = null;
  }

  return {
    appId: APP_ID,
    eventName: 'page_view',
    environment: detectEnvironment(),
    deviceId: getDeviceId(),
    metadata: {
      path,
      referrer,
      ...utmMetadata(),
    },
  };
}

async function sendViaFetch(body: string, retriesLeft: number): Promise<void> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    try {
      await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        signal: controller.signal,
        keepalive: true,
      });
    } finally {
      clearTimeout(timer);
    }
  } catch {
    if (retriesLeft > 0) {
      void sendViaFetch(body, retriesLeft - 1);
    }
    // otherwise swallow — analytics must never crash the page
  }
}

function send(payload: BeaconPayload): void {
  try {
    if (payload.environment === 'development') {
      console.debug('[swapanalytics] page_view', payload);
    }
  } catch {
    // ignore console failures
  }

  try {
    const body = JSON.stringify(payload);

    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      const delivered = navigator.sendBeacon(ENDPOINT, blob);
      if (delivered) return;
    }

    void sendViaFetch(body, 1);
  } catch {
    // analytics must never crash the page
  }
}

function schedule(cb: () => void): void {
  try {
    const w = window as Window & {
      requestIdleCallback?: (fn: () => void, opts?: { timeout: number }) => number;
    };
    if (typeof w.requestIdleCallback === 'function') {
      w.requestIdleCallback(cb, { timeout: 2000 });
      return;
    }
  } catch {
    // fall through to timeout below
  }
  try {
    setTimeout(cb, 0);
  } catch {
    // give up silently — analytics must never throw
  }
}

/**
 * Record a page view for `path`. Safe to call at app boot (first paint) and
 * on every SPA route change — identical consecutive paths are deduped so
 * redundant calls (re-renders, no-op navigations) don't double-fire.
 */
export function recordPageView(path: string): void {
  try {
    if (typeof window === 'undefined') return;
    if (path === lastPath) return;
    lastPath = path;
    schedule(() => {
      try {
        send(buildPayload(path));
      } catch {
        // never throw into the app
      }
    });
  } catch {
    // never throw into the app
  }
}
